import { notificationInstance } from '@/shared/api/api-instances/notification/api-instance';
import type { NotificationStatus } from '@/entities/notifications/notification';
import { Page } from '@/shared/api/pagination';
import type { NoticeDTO } from '@/entities/notifications/notification';
import type { NotificationCountSchema } from '@/entities/notifications/notification';
import type {
  NotificationResponse,
  PageNotificationListResponse,
} from '@/entities/notifications/notification';
import type { AxiosRequestConfig } from 'axios';
import { authInstance } from '@/shared/api/api-instances/auth/api-instance';

interface GetNotificationsProps {
  status: NotificationStatus;
  size?: number;
  page?: number;
  filer?: string;
  sort?: string;
  limit?: number;
  offset?: number;
}

export class NotificationsApi {
  static getNotificationCount(
    status: NotificationStatus,
    options: AxiosRequestConfig = {},
  ) {
    return authInstance.get<NotificationCountSchema>(
      `/user/notifications/count`,
      {
        ...options,
        params: {
          status: status,
        },
      },
    );
  }

  static getNotifications(
    params?: GetNotificationsProps,
    options: AxiosRequestConfig = {},
  ) {
    return authInstance.get<PageNotificationListResponse>(
      `/user/notifications`,
      {
        ...options,
        params,
      },
    );
  }

  static getNotificationsLog(
    params?: GetNotificationsProps,
    options: AxiosRequestConfig = {},
  ) {
    return authInstance.get<PageNotificationListResponse>(
      `/user/notifications`,
      {
        ...options,
        params,
      },
    );
  }

  static getNotification(id: string, options: AxiosRequestConfig = {}) {
    return authInstance.get<NotificationResponse>(
      `/user/notification/${id}`,
      options,
    );
  }

  static setNotificationToViewed(id: string, options: AxiosRequestConfig = {}) {
    return authInstance.put(`/user/notification/viewed/${id}`, null, options);
  }

  static setAllNotificationsToViewed(options: AxiosRequestConfig = {}) {
    return authInstance.put(`/user/notification/viewed/all`, null, options);
  }

  static getAlertManagerConfig(options: AxiosRequestConfig = {}) {
    return notificationInstance.get<{ config: string }>(
      `/alertmanager/config`,
      options,
    );
  }

  static updateAlertManagerConfig(
    payload: { config: string },
    options: AxiosRequestConfig = {},
  ) {
    return notificationInstance.put<{ config: string }>(
      `/alertmanager/config`,
      payload,
      options,
    );
  }

  static getInhibitRules(options: AxiosRequestConfig = {}) {
    return notificationInstance.get<{ config: string }>(
      `/inhibit_rules`,
      options,
    );
  }

  static updateInhibitRules(
    payload: { config: string },
    options: AxiosRequestConfig = {},
  ) {
    return notificationInstance.put<{ config: string }>(
      `/inhibit_rules`,
      payload,
      options,
    );
  }

  static getGeneralSettingsConfig(options: AxiosRequestConfig = {}) {
    return notificationInstance.get<{ config: string }>(
      `/global_config`,
      options,
    );
  }

  static updateGeneralSettingsConfig(
    payload: { config: string },
    options: AxiosRequestConfig = {},
  ) {
    return notificationInstance.put<{ config: string }>(
      `/global_config`,
      payload,
      options,
    );
  }

  static getTemplateConfig(options: AxiosRequestConfig = {}) {
    return notificationInstance.get<{ config: string }>(`/template`, options);
  }

  static updateTemplateConfig(
    payload: { config: string },
    options: AxiosRequestConfig = {},
  ) {
    return notificationInstance.put<{ config: string }>(
      `/template`,
      payload,
      options,
    );
  }

  static getNotices(options?: AxiosRequestConfig) {
    return authInstance.get<Page<NoticeDTO>>('/user/notices', options);
  }

  static getNotice(id: string, options?: AxiosRequestConfig) {
    return authInstance.get<NoticeDTO>(`/user/notice/${id}`, options);
  }

  static deleteNotice(id: string, options?: AxiosRequestConfig) {
    return authInstance.delete<string>(`/user/notice/${id}`, options);
  }

  static deleteNotices(options?: AxiosRequestConfig) {
    return authInstance.delete<string>(`/user/notices`, options);
  }

  static getNoticesCount(options?: AxiosRequestConfig) {
    return authInstance.get<{ count: string }>(`/user/notices/count`, options);
  }
}